export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const getFrameEase = (baseEase, deltaMs, referenceFrameMs = 1000 / 60) => {
  const safeEase = clamp(baseEase, 0, 1);
  const safeDelta = Math.max(0, Number.isFinite(deltaMs) ? deltaMs : referenceFrameMs);

  return 1 - (1 - safeEase) ** (safeDelta / referenceFrameMs);
};

export const getDominantSlotIndex = (opacities, fallbackIndex = 0) => {
  if (!opacities.length) {
    return -1;
  }

  return opacities.reduce((dominantIndex, opacity, index) => {
    const dominantOpacity = opacities[dominantIndex] ?? Number.NEGATIVE_INFINITY;

    if (opacity > dominantOpacity) {
      return index;
    }

    if (opacity === dominantOpacity && index === fallbackIndex) {
      return index;
    }

    return dominantIndex;
  }, clamp(fallbackIndex, 0, opacities.length - 1));
};

export const getExpertiseMediaAssetKey = (dataset = {}) => [
  dataset.expertiseMediaType || '',
  dataset.expertiseVideoSrc || '',
  dataset.expertiseImgWebp || '',
  dataset.expertiseImgSrc || '',
].join('|');

export const shouldPreventExpertisePointerFocus = (pointerType) =>
  pointerType !== 'touch';

export const getViewportTarget = ({
  layerSize,
  point,
  side,
  viewport,
  safePadding,
  horizontalBias,
  verticalBias,
}) => {
  const maxX = Math.max(safePadding, viewport.width - layerSize.width - safePadding);
  const maxY = Math.max(safePadding, viewport.height - layerSize.height - safePadding);
  const mediaXBias = side === 'left' ? horizontalBias.left : horizontalBias.right;

  return {
    x: clamp(point.x - layerSize.width * mediaXBias, safePadding, maxX),
    y: clamp(point.y - layerSize.height * verticalBias, safePadding, maxY),
  };
};

export const getCenteredViewportTarget = ({
  layerSize,
  point,
  viewport,
  safePadding,
}) => getViewportTarget({
  layerSize,
  point,
  side: 'right',
  viewport,
  safePadding,
  horizontalBias: { left: 0.5, right: 0.5 },
  verticalBias: 0.5,
});

export const getElementLayoutSize = (
  element,
  fallback = { width: 0, height: 0 }
) => {
  const width = Number(element?.offsetWidth);
  const height = Number(element?.offsetHeight);

  return {
    width: Number.isFinite(width) && width > 0 ? width : fallback.width,
    height: Number.isFinite(height) && height > 0 ? height : fallback.height,
  };
};

export const getRowBoundedViewportTarget = ({
  layerSize,
  point,
  rowRect,
  viewport,
  safePadding,
  verticalRange,
}) => {
  const rowCenterY = rowRect.top + rowRect.height / 2;
  const boundedPoint = {
    x: point.x,
    y: clamp(point.y, rowCenterY - verticalRange, rowCenterY + verticalRange),
  };

  return getCenteredViewportTarget({
    layerSize,
    point: boundedPoint,
    viewport,
    safePadding,
  });
};

export const getStaggeredAutoplayDelay = (
  index,
  baseDelay,
  staggerDelay
) => {
  const safeIndex = Number.isFinite(index) ? Math.max(0, Math.floor(index)) : 0;
  const safeBaseDelay = Number.isFinite(baseDelay) ? Math.max(0, baseDelay) : 0;
  const safeStaggerDelay = Number.isFinite(staggerDelay)
    ? Math.max(0, staggerDelay)
    : 0;

  return safeBaseDelay + safeIndex * safeStaggerDelay;
};

export const getMediaRotation = (
  side,
  velocityX,
  flattenVelocity,
  baseRotation,
  flattenAmount
) => {
  const signedRotation = side === 'left' ? -baseRotation : baseRotation;
  const flattenRatio = clamp(Math.abs(velocityX) / flattenVelocity, 0, 1);
  const rotation = signedRotation * (1 - flattenRatio * flattenAmount);

  return Math.round(rotation * 1000000) / 1000000;
};

export const getMediaRotationEase = (
  renderedRotation,
  targetRotation,
  flattenEase,
  restoreEase
) => Math.abs(targetRotation) < Math.abs(renderedRotation)
  ? flattenEase
  : restoreEase;

const roundGeometryValue = (value) => {
  const rounded = Math.round(value * 1000000) / 1000000;

  return Object.is(rounded, -0) ? 0 : rounded;
};

export const getRotatedRectCorners = ({
  x,
  y,
  width,
  height,
  rotation,
  scale = 1,
}) => {
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const radians = rotation * Math.PI / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const safeScale = Math.max(0, Number.isFinite(scale) ? scale : 1);
  const halfWidth = width * safeScale / 2;
  const halfHeight = height * safeScale / 2;

  return [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight },
  ].map((point) => ({
    x: roundGeometryValue(centerX + point.x * cosine - point.y * sine),
    y: roundGeometryValue(centerY + point.x * sine + point.y * cosine),
  }));
};

export const getSvgPolygonPoints = (corners) => corners
  .map((point) => `${roundGeometryValue(point.x)},${roundGeometryValue(point.y)}`)
  .join(' ');

export const shouldSpawnExpertiseTrail = ({
  point,
  lastPoint,
  now,
  lastTime,
  distanceThreshold,
  delayThreshold,
}) => {
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    return false;
  }

  if (!lastPoint) {
    return true;
  }

  const distance = Math.hypot(point.x - lastPoint.x, point.y - lastPoint.y);

  return distance >= distanceThreshold && now - lastTime >= delayThreshold;
};

export const getWrappedGalleryIndex = (index, length) => {
  if (length <= 0) {
    return -1;
  }

  return ((index % length) + length) % length;
};

export const getNextGalleryIndex = (currentIndex, length) =>
  getWrappedGalleryIndex(currentIndex + 1, length);

export const getReusableTrailSlotIndex = (slots) => {
  if (!slots.length) {
    return -1;
  }

  const inactiveIndex = slots.findIndex((slot) => !slot.active);

  if (inactiveIndex !== -1) {
    return inactiveIndex;
  }

  return slots.reduce((oldestIndex, slot, index) => (
    slot.startedAt < slots[oldestIndex].startedAt ? index : oldestIndex
  ), 0);
};

export const getCurrentTrailSlotIndex = (slots) => slots.findIndex(
  (slot) => slot.active && slot.state === 'current'
);

export const promoteTrailSlot = (slots, nextIndex) => {
  const previousIndex = getCurrentTrailSlotIndex(slots);

  if (previousIndex === nextIndex) {
    return -1;
  }

  if (previousIndex !== -1) {
    slots[previousIndex].state = 'retiring';
  }

  if (slots[nextIndex]) {
    slots[nextIndex].state = 'current';
  }

  return previousIndex;
};

export const getLocalClipPolygon = (corners, bounds) => {
  const points = corners.map((point) => {
    const x = roundGeometryValue(point.x - bounds.left);
    const y = roundGeometryValue(point.y - bounds.top);

    return `${x}px ${y}px`;
  });

  return `polygon(${points.join(', ')})`;
};

export const isRectOutsideViewport = (rect, viewportHeight) => (
  rect.bottom <= 0 || rect.top >= viewportHeight
);
